import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { settingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Save, Plus, X, Bell, Building, AlertCircle } from 'lucide-react';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    company_phone: '',
    company_email: '',
    notification_emails: [],
    notification_phones: []
  });
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings({
        company_name: response.data.company_name || '',
        company_phone: response.data.company_phone || '',
        company_email: response.data.company_email || '',
        notification_emails: response.data.notification_emails || [],
        notification_phones: response.data.notification_phones || []
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    if (newEmail && !settings.notification_emails.includes(newEmail)) {
      setSettings({
        ...settings,
        notification_emails: [...settings.notification_emails, newEmail]
      });
      setNewEmail('');
    }
  };

  const removeEmail = (email) => {
    setSettings({
      ...settings,
      notification_emails: settings.notification_emails.filter(e => e !== email)
    });
  };

  const addPhone = () => {
    if (newPhone && !settings.notification_phones.includes(newPhone)) {
      setSettings({
        ...settings,
        notification_phones: [...settings.notification_phones, newPhone]
      });
      setNewPhone('');
    }
  };

  const removePhone = (phone) => {
    setSettings({
      ...settings,
      notification_phones: settings.notification_phones.filter(p => p !== phone)
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">Configure your business settings</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-lime-500 hover:bg-lime-600 text-black"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Mocked Notification Alert */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Notifications are currently MOCKED</p>
              <p className="text-sm text-yellow-700 mt-1">
                To enable real SMS and email notifications, add the following to your backend .env file:
              </p>
              <code className="block mt-2 p-2 bg-white rounded text-xs text-gray-700">
                TWILIO_ACCOUNT_SID=your_sid<br/>
                TWILIO_AUTH_TOKEN=your_token<br/>
                TWILIO_PHONE_NUMBER=+1234567890<br/>
                SENDGRID_API_KEY=your_key<br/>
                SENDER_EMAIL=your@email.com
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Info */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="w-5 h-5" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                placeholder="XA's Lawns"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={settings.company_phone}
                onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="company-phone-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={settings.company_email}
                onChange={(e) => setSettings({...settings, company_email: e.target.value})}
                placeholder="contact@xaslawns.com"
                data-testid="company-email-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Recipients */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Recipients
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-gray-500">
            Add email addresses and phone numbers to receive notifications when new estimate requests come in.
          </p>

          {/* Email Recipients */}
          <div>
            <Label className="mb-3 block">Email Recipients</Label>
            <div className="flex gap-2 mb-3">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Add email address"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                data-testid="add-email-input"
              />
              <Button type="button" onClick={addEmail} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.notification_emails.map((email) => (
                <span key={email} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                  {email}
                  <button onClick={() => removeEmail(email)} className="text-gray-500 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
              {settings.notification_emails.length === 0 && (
                <span className="text-sm text-gray-400">No email recipients added</span>
              )}
            </div>
          </div>

          {/* Phone Recipients */}
          <div>
            <Label className="mb-3 block">SMS Recipients</Label>
            <div className="flex gap-2 mb-3">
              <Input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Add phone number (+1234567890)"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhone())}
                data-testid="add-phone-input"
              />
              <Button type="button" onClick={addPhone} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.notification_phones.map((phone) => (
                <span key={phone} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                  {phone}
                  <button onClick={() => removePhone(phone)} className="text-gray-500 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
              {settings.notification_phones.length === 0 && (
                <span className="text-sm text-gray-400">No SMS recipients added</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
